import React, { Component } from 'react';
import Inherit from '../../../core/inherit';
import withHooks from '../../../core/withHooks';
import UserController from './user.controller';
import Page from '../../common/page/Page';
import { SCOPES, userHasScope } from '../../../constants/scopes.constants';
import SplitLayout from '../../common/layouts/splitLayout/SplitLayout';
import { SplitLayoutSide } from '../../common/layouts/splitLayout/splitLayout.constants';
import UserHeader from './elements/UserHeader';
import UserKpiCards from './elements/UserKpiCards';
import UserDetailsCard from './elements/UserDetailsCard';
import PayrollSyncStatusCard from './elements/PayrollSyncStatusCard';
import PayrollConnectionsPanel from '../../common/payrollConnections/PayrollConnectionsPanel';
import ReconcileAccountCard from './elements/ReconcileAccountCard';
import ReconcileAccountModal from './elements/ReconcileAccountModal';
import { UserActionConfirmModal } from '../../common/userActions';
import TransactionHistoryCard from './elements/TransactionHistoryCard';
import UserFunnelAnchor from './elements/UserFunnelAnchor';
import UserFunnelCard from './elements/UserFunnelCard';
import CreateTicketModal from '../../support/elements/CreateTicketModal';
import styles from './styles/user.module.scss';
import { DEFAULT_PAGE_SIZE, SYNC_DETAIL_IDLE } from '../../reports/reports.constants';

/**
 * User Journey detail page (`/users/user?userId=...`). Composes the
 * common `Page` shell with three Figma sections (5747:1664):
 *  - `UserHeader`           — avatar + name + meta chips + 2 actions.
 *  - `UserKpiCards`         — 3 KPI tiles (first one highlighted).
 *  - `SplitLayout`          — left rail with details + sync cards,
 *                             right column with transaction history and,
 *                             underneath it, the onboarding journey.
 *
 * `UserFunnelCard` is fed by its own endpoint (an Athena scan) and carries
 * its own loading / failure states, so the rest of the page renders without
 * waiting on it.
 *
 * SplitLayout receives `sideVariant={SplitLayoutSide.LEFT}` so the
 * narrow rail lands on the left like the Figma reference (the
 * component defaults to the right side, which is what the auth
 * pages need). The 33/67 split itself comes from the page SCSS.
 *
 * @extends {UserController}
 * @extends {Component}
 */
class User extends Inherit(UserController, Component) {
    state = {
        loading: true,
        user: {},
        consumer: {},
        syncDetail: { ...SYNC_DETAIL_IDLE }, // same shape as the Reports panel (SyncDetail)
        payrollConnections: { loading: true, error: null, data: null, timezone: null },
        connectionEvents: {},
        expandedConnections: [],
        isRestricted: false,
        restrictLoading: false,
        markCompleteLoading: false,
        deleteLastLoading: false,
        funnel: null,
        funnelLoading: false,
        funnelError: null,
        funnelRetryable: true,
        // The payroll-link failure history behind the funnel's error row. Its
        // own endpoint and its own Athena scan, so it stays empty until an
        // agent actually opens that row.
        linkAttemptsOpen: false,
        linkAttempts: null,
        linkAttemptsHasError: false,
        linkAttemptsLoading: false,
        linkAttemptsError: null,
        linkAttemptsRetryable: true,
        reconcileModalOpen: false,
        reconcileLoading: false,
        transactions: [],
        transactionsLoading: false,
        transactionsPageInfo: { current: 1, perPage: DEFAULT_PAGE_SIZE },
        // Controlled sort/filter for the transactions table. Both are applied
        // server-side; holding them here is what keeps the column headers
        // showing the ordering the server actually used. Null means the user has
        // not sorted — the endpoint still returns rows newest-first, but no
        // header claims to be the reason.
        transactionsSort: null,
        transactionsType: null,
        // The zone the ledger endpoint declared for the last payload; null
        // until the first response, when the table falls back to its default.
        transactionsTimezone: null,
        // A failed fetch is not an empty ledger. Kept apart so the table's
        // empty state can say which of the two it is.
        transactionsFailed: false,
        transactionTicketModalOpen: false,
        transactionTicketSubmitting: false,
        transactionTicketSubject: '',
        transactionTicketBody: '',
        transactionTicketAttachments: [],
        transactionTicketCategory: null,
        transactionTicketPriority: null,
        transactionTicketLedgerId: null,
        transactionTicketClientId: null,
    };

    render() {
        const {
            loading,
            consumer,
            syncDetail,
            payrollConnections,
            connectionEvents,
            expandedConnections,
            isRestricted,
            restrictLoading,
            markCompleteLoading,
            deleteLastLoading,
            funnel,
            funnelLoading,
            funnelError,
            funnelRetryable,
            linkAttemptsOpen,
            linkAttempts,
            linkAttemptsHasError,
            linkAttemptsLoading,
            linkAttemptsError,
            linkAttemptsRetryable,
            reconcileModalOpen,
            reconcileLoading,
            transactions,
            transactionsLoading,
            transactionsPageInfo,
            transactionsSort,
            transactionsType,
            transactionsTimezone,
            transactionsFailed,
            transactionTicketModalOpen,
            transactionTicketSubmitting,
            transactionTicketSubject,
            transactionTicketBody,
            transactionTicketAttachments,
            transactionTicketCategory,
            transactionTicketPriority,
        } = this.state;

        // Derive view labels through the controller so the helpers
        // stay testable in isolation and any handler that needs the
        // same values can reuse them via `this.<method>()`.
        const name = consumer?.name || '';
        const nameParts = this.formatUserNameHighlight(name);
        const payPeriod = this.parsePayPeriod(consumer?.payPeriod);

        // Money presentation is resolved once here, through MoneyController, and
        // handed down. No child component decides a colour or a format.
        const repayment = this.describeRepaymentStatus(consumer);
        const formatMoney = (value) => this.formatMoney(value);
        const moneyClassName = (tone) => this.getMoneyColorClassName(tone);
        const toneForOwedAmount = (amount) => this.toneForOwedAmount(amount);
        const toneForSettledAmount = (amount) => this.toneForSettledAmount(amount);

        // Work type comes from the sync report when available. The
        // backend will eventually surface this directly on the
        // consumer payload (TODO).
        const workTypeLabel = syncDetail.report?.salaried === true
            ? 'Salaried'
            : syncDetail.report?.salaried === false
                ? 'Hourly'
                : null;

        // The payroll-connections summary is the first source that actually
        // knows which platform a member is on; the consumer payload is the
        // fallback.
        const payrollSummary = this.resolvePayrollHeaderSummary();
        const payrollSystemLabel = payrollSummary?.platformLabel || consumer?.payrollSystem || null;
        // `sync-status` describes one sync row, so its dimension pills belong to
        // exactly one connection panel. Whenever that panel is absent *or shut*
        // the card renders standalone in the rail — the two admin actions live
        // on it, and a collapsed host would otherwise take them off the page.
        const syncStatusHost = this.resolveSyncStatusHost();
        const renderSyncStatusCard = (renderShell) => (
            <PayrollSyncStatusCard
                detail={syncDetail}
                renderShell={renderShell}
                onRetry={() => this.reloadSyncStatus()}
                markCompleteLoading={markCompleteLoading}
                deleteLastLoading={deleteLastLoading}
                onMarkComplete={() => this.handleMarkSyncComplete()}
                onDeleteLast={() => this.handleDeleteLastSync()}
                canDeleteSync={canDeleteSync}
                canMarkSyncComplete={canMarkSyncComplete}
            />
        );
        // Only the transaction-history ticket action is left on this flag; the
        // header and sync-card actions each have their own scope below.
        const canAction = userHasScope(this.data?.user, SCOPES.TEAM_INVITE);
        const canOpenSso = this.canOpenSso();
        const canViewFunnel = this.canViewFunnel();
        const canReconcile = this.canReconcileAccount();
        const canRestrict = this.canRestrictUser();
        const canDeleteSync = this.canDeleteSync();
        const canMarkSyncComplete = this.canMarkSyncComplete();

        return (
            <Page
                isLoading={loading}
                scope={SCOPES.DASHBOARD_VIEW}
                notificationContextHolder={this.props.notificationContextHolder}
            >
                <div className={styles.page}>
                    <UserHeader
                        name={name}
                        nameParts={nameParts}
                        payPeriod={payPeriod}
                        workTypeLabel={workTypeLabel}
                        payrollSystemLabel={payrollSystemLabel}
                        lastConnectionLabel={payrollSummary?.text || null}
                        isRestricted={isRestricted}
                        restrictLoading={restrictLoading}
                        onOpenSso={() => this.handleOpenSso()}
                        onToggleRestrict={() => this.handleToggleRestrict()}
                        canRestrict={canRestrict}
                        canOpenSso={canOpenSso}
                    />
                    {canViewFunnel ? (
                        <UserFunnelAnchor
                            funnel={funnel}
                            loading={funnelLoading}
                            error={funnelError}
                        />
                    ) : null}
                    <UserKpiCards
                        consumer={consumer}
                        repayment={repayment}
                        formatMoney={formatMoney}
                        moneyClassName={moneyClassName}
                        toneForOwedAmount={toneForOwedAmount}
                        toneForSettledAmount={toneForSettledAmount}
                    />
                    <SplitLayout
                        key="user-journey"
                        className={styles.splitRoot}
                        contentClassName={styles.splitContent}
                        sideClassName={styles.splitSide}
                        sideVariant={SplitLayoutSide.LEFT}
                        side={(
                            <>
                                <UserDetailsCard consumer={consumer} payPeriod={payPeriod} />
                                <PayrollConnectionsPanel
                                    connections={payrollConnections?.data}
                                    timezone={payrollConnections?.timezone}
                                    loading={payrollConnections?.loading}
                                    error={payrollConnections?.error}
                                    eventsByKey={connectionEvents}
                                    expandedKeys={expandedConnections}
                                    onRetry={() => this.reloadPayrollConnections()}
                                    onToggleConnection={(key, expanded) => this.handleToggleConnection(key, expanded)}
                                    onLoadMoreEvents={(key) => this.handleLoadMoreConnectionEvents(key)}
                                    onRetryEvents={(key) => this.handleRetryConnectionEvents(key)}
                                    renderConnectionDetail={(key) =>
                                        syncStatusHost.placed && syncStatusHost.key === key ? renderSyncStatusCard(false) : null
                                    }
                                />
                                {!syncStatusHost.hosted ? renderSyncStatusCard(true) : null}
                                {canReconcile ? (
                                    <ReconcileAccountCard
                                        loading={reconcileLoading}
                                        onReconcile={() => this.handleOpenReconcileModal()}
                                    />
                                ) : null}
                            </>
                        )}
                    >
                        <TransactionHistoryCard
                            rows={transactions}
                            loading={transactionsLoading}
                            pageInfo={transactionsPageInfo}
                            onTableChange={this.handleTransactionsTableChange.bind(this)}
                            sort={transactionsSort}
                            typeFilter={transactionsType}
                            timezone={transactionsTimezone}
                            failed={transactionsFailed}
                            onRetry={() => this.retryTransactions()}
                            onOpenTicket={(record) => this.handleOpenTransactionTicket(record)}
                            canAction={canAction}
                        />
                        {canViewFunnel ? (
                            <UserFunnelCard
                                funnel={funnel}
                                loading={funnelLoading}
                                error={funnelError}
                                onRetry={funnelRetryable ? () => this.loadFunnel() : null}
                                attemptsOpen={linkAttemptsOpen}
                                attempts={linkAttempts}
                                attemptsHasError={linkAttemptsHasError}
                                attemptsLoading={linkAttemptsLoading}
                                attemptsError={linkAttemptsError}
                                onOpenAttempts={() => this.openLinkAttempts()}
                                onCloseAttempts={() => this.closeLinkAttempts()}
                                onRetryAttempts={
                                    linkAttemptsRetryable ? () => this.loadLinkAttempts() : null
                                }
                            />
                        ) : null}
                    </SplitLayout>
                    <CreateTicketModal
                        open={transactionTicketModalOpen}
                        submitting={transactionTicketSubmitting}
                        subject={transactionTicketSubject}
                        body={transactionTicketBody}
                        attachments={transactionTicketAttachments}
                        category={transactionTicketCategory}
                        priority={transactionTicketPriority}
                        title="New support ticket"
                        description="Create a support ticket linked to this transaction."
                        onSubjectChange={(value) => this.setState({ transactionTicketSubject: value })}
                        onBodyChange={(value) => this.setState({ transactionTicketBody: value })}
                        onAttachmentsChange={(entries) => this.setState({ transactionTicketAttachments: entries })}
                        onCategoryChange={(value) => this.setState({ transactionTicketCategory: value })}
                        onPriorityChange={(value) => this.setState({ transactionTicketPriority: value })}
                        onCancel={() => this.handleCloseTransactionTicketModal()}
                        onSubmit={() => this.handleSubmitTransactionTicket()}
                    />
                    <ReconcileAccountModal
                        open={reconcileModalOpen}
                        loading={reconcileLoading}
                        onCancel={() => this.handleCloseReconcileModal()}
                        onConfirm={() => this.handleConfirmReconcile()}
                    />
                    <UserActionConfirmModal
                        confirm={this.userActionConfirm}
                        onCancel={() => this.cancelUserAction()}
                        onConfirm={() => this.userActionConfirmSubmit()}
                    />
                </div>
            </Page>
        );
    }
}

export default withHooks(User);
